import './FacilityImageUpload.scss';

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { getClient } from 'src/module/authentication/selector';
import { updateClientAvatar, deleteClientAvatar } from 'src/module/authentication/action';
import { getClientAvatar } from 'src/service/api';
import toast from 'src/utils/toast';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';

export default function FacilityImageUpload () {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const client = useSelector(getClient);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const clientId = client.get('id');
  const avatarId = client.get('avatar_id');
  const avatarUrl = client.get('avatar_url');

  // Load existing avatar
  useEffect(() => {
    const loadAvatar = async () => {
      if (avatarId && !avatarUrl && clientId) {
        try {
          const action = { currentClientId: clientId, clientId: clientId };
          const blobUrl = await getClientAvatar(action, clientId);
          setPreviewUrl(blobUrl);
        } catch (error) {
          toast.error(t('toast.failedToLoadAvatar'));
        }
      } else if (avatarUrl) {
        setPreviewUrl(avatarUrl);
      }
    };

    loadAvatar();
  }, [avatarId, avatarUrl, clientId]);

  const handleFileUpload = async (file: File) => {
    // Validate file type - only PNG and SVG
    const allowedTypes = ['image/png', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('toast.selectPngOrSvg'));
      return;
    }

    // Create preview URL
    const fileUrl = URL.createObjectURL(file);
    setPreviewUrl(fileUrl);

    // Upload the file
    setIsUploading(true);
    try {
      dispatch(updateClientAvatar(file));
    } catch (error) {
      toast.error(t('toast.failedToUploadImage'));
      setPreviewUrl(avatarUrl || null); // Revert to previous image
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    maxSize: 5 * 1024 * 1024, // 5MB
    multiple: false,
    onDrop: async (acceptedFiles: File[], fileRejections) => {
      if (fileRejections.length > 0) {
        const error = fileRejections[0]?.errors[0];
        if (error?.code === 'file-too-large') {
          toast.error(t('toast.imageTooLarge'));
        } else if (error?.code === 'file-invalid-type') {
          toast.error(t('toast.selectPngOrSvg'));
        } else {
          toast.error(t('toast.invalidFileSelected'));
        }
        return;
      }

      if (acceptedFiles.length === 0) {
        toast.error(t('toast.noValidImageSelected'));
        return;
      }

      const file = acceptedFiles[0];
      await handleFileUpload(file);
    }
  });

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleRemoveImage = () => {
    try {
      dispatch(deleteClientAvatar());
      setPreviewUrl(null);
    } catch (error) {
      toast.error(t('toast.failedToRemoveImage'));
    }
  };

  const dropzoneStyles = {
    border: '2px dashed',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'border-color 0.3s ease',
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isDragActive ? 'action.hover' : 'transparent',
    borderColor: isDragReject ? 'error.main' : isDragAccept ? 'success.main' : variables.greyLight
  };

  return (
    <div className="facility-image-upload" style={{ padding: '16px' }}>
      <Typography
        variant="body1"
        component="label"
        sx={{
          display: 'block',
          lineHeight: 1.43,
          textTransform: 'uppercase',
          marginBottom: '8px',
          fontSize: '12px',
          fontWeight: 600,
          color: 'text.secondary',
        }}
      >
        {t('facilityImage.title')}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '12px' }}>
        {t('facilityImage.description')}
      </Typography>

      <Box sx={{ mt: 1 }}>
        {previewUrl ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={previewUrl}
              alt={t('facilityImage.preview')}
              sx={{
                width: '100%',
                maxWidth: 480,
                maxHeight: 220,
                objectFit: 'cover',
                border: `2px solid ${variables.greyLight}`,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                padding: '10px'
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudUploadIcon />}
                component="label"
                disabled={isUploading}
              >
                {isUploading ? t('facilityImage.uploading') : t('facilityImage.changeImage')}
                <input
                  type="file"
                  accept=".png,.svg,image/png,image/svg+xml"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleRemoveImage}
                disabled={isUploading}
              >
                {t('facilityImage.remove')}
              </Button>
            </Box>
          </Box>
        ) : (
          <div {...getRootProps()} style={dropzoneStyles}>
            <input {...getInputProps()} id="facility-image-input" />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="textSecondary">
              {isDragActive
                ? t('facilityImage.dropHere')
                : t('facilityImage.dragAndDrop')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {t('facilityImage.supportedFormats')}
            </Typography>
          </div>
        )}
      </Box>
    </div>
  );
}
